// =============================================================================
// Sample-value type detection
// =============================================================================
//
// The suggestions endpoint returns sample values as JSON scalars, but
// numeric/boolean fields routinely come back as their string representation
// (e.g. `"100"`, `"true"`). Each detector accepts both the native primitive
// shape and the stringified shape, so the inferred valueType lands the same
// regardless of how the upstream serialised it.
//
// =============================================================================

/**
 * Whether `value` is a finite number or a finite-number string. Empty /
 * whitespace strings don't count (`Number('') === 0` would otherwise misclassify
 * them) and booleans are never number-like.
 */
export function isNumberLike(value: unknown): boolean {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (typeof value === 'string') {
    if (value.trim() === '') {
      return false;
    }
    return Number.isFinite(Number(value));
  }
  return false;
}

// Boolean strings only count when they're exactly `"true"` / `"false"`
// (case-insensitive). Matches the BOOLEAN heuristic in `dataviz-experimental`'s
// inferFieldType so the type label stays consistent whether or not `valueType`
// was set upstream.
const BOOLEAN_STRING_SET = new Set(['true', 'false']);

/** Whether `value` is a boolean or a `"true"` / `"false"` string (case-insensitive). */
export function isBooleanLike(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return true;
  }
  if (typeof value === 'string') {
    return BOOLEAN_STRING_SET.has(value.toLowerCase());
  }
  return false;
}
